import {
  ConsumerType,
  DealSellingVehicle,
  DealStages,
  DealType,
  ListMode,
  Participant,
  TradeVehicle
} from "@coxauto-ui/cxm/interfaces";
import { CcxmReusableLabelledInsightDisplay } from "@coxauto-ui/ccxm/reusable/labelled-insight-display";
import { trackInNewRelic } from "@coxauto-ui/core-third-party-newrelic";
import { Button } from "@interstate/components/Button";
import { LinkChip } from "@interstate/components/Chip";
import { MouseEvent, ReactNode, useCallback, useContext, useMemo } from "react";
import classnames from "classnames";
import { ImageWithFallback } from "@coxauto-ui/core/components";
import { CheckCircleIcon } from "@interstate/components/Icons/CheckCircleIcon";
import EmployeesInsightDisplay from "./employees-insight-display";
import { CustomerCardTabTypes } from "@coxauto-ui/util/derive-customer-view-link";
import { ArrowTopRightOnSquareIcon } from "@interstate/components/Icons/ArrowTopRightOnSquareIcon";
import { RoutingContext } from "@coxauto-ui/cxm/routing";
import { ProgressChecker, Step } from "@coxauto-ui/progress-checker";
import {
  AllowedField,
  mapDealChecker
} from "@coxauto-ui/core/utility/map-deal-checker";
import { DateDistanceToNow } from "@coxauto-ui/core-components";
import { UserCircleIcon } from "@interstate/components/Icons/UserCircleIcon";
import { BuildingOfficeIcon } from "@interstate/components/Icons/BuildingOfficeIcon";
import { useCoxAnalytics } from "@coxauto-ui/ccxm/util/use-cox-analytics-page-tracking";
import { getDealInsightsRecordsState } from "@coxauto-ui/ccxm/store";
import { shallowEqual, useSelector } from "react-redux";
import { getFilterState } from "@coxauto-ui/util/get-selected-filter-categories";

export interface HandleCustomerRecordClick {
  consumerId: string;
  dealId: string;
  recordRow: number;
  consumerType?: ConsumerType;
  defaultCustomerTab?: CustomerCardTabTypes;
}

/* eslint-disable-next-line */
export interface CardProps {
  consumerId: string;
  dealStages?: DealStages;
  consumerType?: ConsumerType;
  consumerFirstName: string | null;
  consumerLastName: string | null;
  persona?: string;
  dealId: string;
  dealType?: DealType;
  isNewDeal?: boolean;
  dealershipId: string;
  dealershipName: string;
  employees: Participant[];
  isAccepted: boolean;
  integratorLastUpdatedUtc: string;
  listMode: ListMode;
  manageDealButtonLink: string | null;
  mostRecentUpdateMessages: string[] | null;
  recordRow: number;
  sellingVehicle: DealSellingVehicle | null;
  showMultiDealButton?: boolean;
  tradeVehicles: TradeVehicle[];
  viewDealButtonLink: string | null;
  handleCustomerRecordClick: (props: HandleCustomerRecordClick) => void;
  handleSelectMultiDeal: (consumerId: string, dealId: string) => void;
  onFetchDealLink?: (dealId: string) => Promise<string | null>;
}

const htmlId = "close-my-deal-card";
const acceptedStatus = "accepted";

const isOpenDealLink = (link: string | null | undefined): boolean => {
  return link?.includes("opendeal.app") ?? false;
};

const mapStatusPill = {
  [acceptedStatus]: {
    color: "#c9eabf",
    id: "accepted",
    text: "Accepted"
  },
  sold: {
    color: "#c9eabf",
    id: "sold",
    text: "Sold"
  },
  lost: {
    color: "#fabaaf",
    id: "lost",
    text: "Lost"
  }
};

function isKeyOfMapStatusPill(key: string): key is keyof typeof mapStatusPill {
  return key in mapStatusPill;
}

export function Card({
  consumerId,
  consumerFirstName,
  consumerLastName,
  consumerType,
  dealId,
  dealType,
  dealershipId,
  dealershipName,
  employees,
  integratorLastUpdatedUtc,
  isAccepted,
  listMode,
  persona,
  manageDealButtonLink,
  mostRecentUpdateMessages,
  dealStages,
  recordRow,
  sellingVehicle,
  showMultiDealButton = false,
  tradeVehicles,
  viewDealButtonLink,
  handleCustomerRecordClick,
  handleSelectMultiDeal,
  isNewDeal,
  onFetchDealLink
}: CardProps) {
  const { selectedCustomerId, selectedMultiDealCustomerId, selectedDealId } =
    useContext(RoutingContext);
  const { pushEvent } = useCoxAnalytics();

  // Get filter data from store and determine filter state
  const { optionFilters, matrixFilters } = useSelector(
    getDealInsightsRecordsState,
    shallowEqual
  );

  const filterState = useMemo(() => {
    return getFilterState({
      optionFilters,
      matrixFilters
    });
  }, [optionFilters, matrixFilters]);

  const dealTracker: Step[] = useMemo(() => {
    if (!dealStages) return [];
    return mapDealChecker(dealStages, [AllowedField.status]);
  }, [dealStages, listMode]);

  const consumerFullName = useMemo(() => {
    if (consumerType === "Business") {
      return consumerLastName;
    }
    return consumerFirstName || consumerLastName
      ? [consumerFirstName || "--", consumerLastName || "--"].join(" ")
      : "--";
  }, [consumerFirstName, consumerLastName]);

  const vehicleFullName = useMemo(() => {
    return sellingVehicle
      ? [
          sellingVehicle.year || "--",
          sellingVehicle.make || "--",
          sellingVehicle.model || "--",
          sellingVehicle.trim || "--"
        ].join(" ")
      : "--";
  }, [sellingVehicle]);

  const vehicleTypeAndStockNumber = useMemo(() => {
    return (
      <>
        <span data-testid={`${htmlId}-${recordRow}-vehicle-type`}>
          {sellingVehicle?.sellingVehicleType || "--"}
        </span>
        <span className="delimiter">|</span>
        {sellingVehicle?.isCustom ? (
          <span data-testid={`${htmlId}-${recordRow}-custom-vehicle`}>
            Custom Vehicle
          </span>
        ) : (
          <span data-testid={`${htmlId}-${recordRow}-stock-number`}>
            {sellingVehicle?.stockNumber
              ? `Stock #${sellingVehicle.stockNumber}`
              : "--"}
          </span>
        )}
      </>
    );
  }, [
    recordRow,
    sellingVehicle?.isCustom,
    sellingVehicle?.sellingVehicleType,
    sellingVehicle?.stockNumber
  ]);

  const mostRecentUpdateMessage = useMemo(() => {
    return (mostRecentUpdateMessages && mostRecentUpdateMessages[0]) || "--";
  }, [mostRecentUpdateMessages]);

  const borderMode = useMemo(() => {
    if (listMode === ListMode.desktopCustomer) {
      const isMultiDeal = selectedMultiDealCustomerId !== null;
      const isCurrentDeal = selectedDealId === dealId;
      const isCurrentCustomerAndDeal =
        selectedCustomerId === consumerId && isCurrentDeal;

      return (isMultiDeal ? isCurrentDeal : isCurrentCustomerAndDeal)
        ? "highlighted"
        : "standard";
    }
    return "standard";
  }, [
    listMode,
    selectedMultiDealCustomerId,
    selectedDealId,
    dealId,
    selectedCustomerId,
    consumerId
  ]);

  const openLink = useCallback((url: string | null) => {
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }, []);

  const resolveManageDealButtonLink = useCallback(async () => {
    if (!manageDealButtonLink) return null;
    if (!isOpenDealLink(manageDealButtonLink) || !onFetchDealLink) {
      return manageDealButtonLink;
    }

    try {
      const updatedLink = await onFetchDealLink(dealId);
      return updatedLink || manageDealButtonLink;
    } catch {
      return manageDealButtonLink;
    }
  }, [dealId, manageDealButtonLink, onFetchDealLink]);

  const onDealCardClick = async () => {
    if (manageDealButtonLink) {
      const resolvedLink = await resolveManageDealButtonLink();
      openLink(resolvedLink);
      return;
    }

    if (viewDealButtonLink) {
      openLink(viewDealButtonLink);
    }
    // Do nothing if neither link is available
  };

  const onCustomerRecordClick = (e: MouseEvent<HTMLButtonElement>) => {
    // Prevent the card click event from firing.
    e.stopPropagation();

    handleCustomerRecordClick({ consumerId, dealId, recordRow, consumerType });
  };

  // interstate does not provide a type for this event
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onMultiDealClick = (e: any) => {
    e.stopPropagation();
    handleSelectMultiDeal(consumerId, dealId);
  };

  const onOpenDealCentralClick = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      trackInNewRelic({
        componentAction: "DealCentralButtonClicked",
        locationLoadedFrom: document.title,
        dealerId: dealershipId,
        destinationUrl: viewDealButtonLink || ""
      });

      // Cox Analytics event
      pushEvent({
        deal: {
          consumerId,
          dealId
        },
        event: {
          action: "Click",
          element: "Manager View Link",
          intention: "Open Manager View from Deal List",
          location: "Deal Central, Deal List",
          tagIndex: "dl-002"
        },
        search: {
          acceptedOffers: filterState?.hasAcceptedOffersFilter || false,
          date: filterState?.hasDateFilter || false,
          dealer: filterState?.hasDealerFilter || false,
          dealPaymentMethod: filterState?.hasDealPaymentMethodFilter || false,
          dealStages: filterState?.hasDealStagesFilter || false,
          dealStatus: filterState?.hasDealStatusFilter || false,
          employee: filterState?.hasEmployeeFilter || false,
          make: filterState?.hasMakeFilter || false,
          resultsRank: recordRow, // Position in Deal List
          vehicleType: filterState?.hasVehicleTypeFilter || false
        },
        vehicle: {
          dealerStockNumber: sellingVehicle?.stockNumber || null,
          make: sellingVehicle?.make || null,
          model: sellingVehicle?.model || null,
          year: sellingVehicle?.year || null
        }
      });

      if (isOpenDealLink(manageDealButtonLink)) {
        e.preventDefault();
        const resolvedLink = await resolveManageDealButtonLink();
        openLink(resolvedLink);
      }
    },
    [
      consumerId,
      dealId,
      dealershipId,
      filterState,
      manageDealButtonLink,
      openLink,
      pushEvent,
      recordRow,
      resolveManageDealButtonLink,
      sellingVehicle,
      viewDealButtonLink
    ]
  );

  const onOpenShowroomClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      trackInNewRelic({
        componentAction: "ShowroomButtonClicked",
        locationLoadedFrom: document.title,
        dealerId: dealershipId,
        destinationUrl: viewDealButtonLink || ""
      });

      // Cox Analytics event
      pushEvent({
        deal: {
          consumerId,
          dealId
        },
        event: {
          action: "Click",
          element: "Sales View Link",
          intention: "Open Sales View from Deal List",
          location: "Deal Central, Deal List",
          tagIndex: "dl-003"
        },
        search: {
          acceptedOffers: filterState?.hasAcceptedOffersFilter || false,
          date: filterState?.hasDateFilter || false,
          dealer: filterState?.hasDealerFilter || false,
          dealPaymentMethod: filterState?.hasDealPaymentMethodFilter || false,
          dealStages: filterState?.hasDealStagesFilter || false,
          dealStatus: filterState?.hasDealStatusFilter || false,
          employee: filterState?.hasEmployeeFilter || false,
          make: filterState?.hasMakeFilter || false,
          resultsRank: recordRow, // Position in Deal List
          vehicleType: filterState?.hasVehicleTypeFilter || false
        },
        vehicle: {
          dealerStockNumber: sellingVehicle?.stockNumber || null,
          make: sellingVehicle?.make || null,
          model: sellingVehicle?.model || null,
          year: sellingVehicle?.year || null
        }
      });
    },
    [
      consumerId,
      dealershipId,
      filterState,
      pushEvent,
      recordRow,
      sellingVehicle,
      viewDealButtonLink
    ]
  );

  const headerContent = useMemo(() => {
    const consumerValue = (
      <span className="consumer-name-container">
        <span
          className="consumer-type-icon"
          data-consumername="consumer-insight-record-type-icon"
          data-testid={`consumer-${recordRow}-type-icon`}
          id={`consumer-${recordRow}-type-icon`}
        >
          {consumerType === "Business" ? (
            <BuildingOfficeIcon
              data-testid={`consumer-${recordRow}-business-icon`}
            />
          ) : (
            <UserCircleIcon
              data-testid={`consumer-${recordRow}-individual-icon`}
            />
          )}
        </span>
        <span
          className="consumer-name"
          data-consumerid={consumerId}
          data-consumername="consumer-insight-record-full-name"
          data-testid={`consumer-${recordRow}-full-name`}
          id={`consumer-${recordRow}-consumer-name`}
        >
          {consumerFullName}
        </span>
      </span>
    );
    const content: {
      className?: string;
      id: string;
      "data-bordermode"?: string;
      "data-consumerid"?: string;
      "data-consumername"?: string;
      "data-testid": string;
      value: ReactNode;
    }[] = [
      {
        className: "consumer-name",
        "data-bordermode": `consumer-${recordRow}-${borderMode}`,
        "data-testid": `consumer-${recordRow}-name-container`,
        id: `${htmlId}-${dealId}-consumer-name`,
        value: consumerValue
      },
      {
        className: "dealership-name",
        "data-testid": `${htmlId}-${recordRow}-dealership-name`,
        id: `${htmlId}-${dealId}-dealership-name`,
        value: dealershipName
      }
    ];

    if (tradeVehicles.length > 0) {
      content.push({
        "data-testid": `${htmlId}-${recordRow}-trade-in`,
        id: `${htmlId}-${dealId}-trade-in`,
        value: (
          <span className="has-trade-in">
            <CheckCircleIcon color="sem.color.on-surface.success.muted" />
            <span>Trade-in</span>
          </span>
        )
      });
    }

    return content;
  }, [
    borderMode,
    consumerFullName,
    consumerId,
    consumerType,
    dealId,
    dealershipName,
    recordRow,
    tradeVehicles.length
  ]);

  const getLastUpdatedUtcValue = useMemo(() => {
    if (persona)
      return (
        <span>
          <span>{persona}</span> (
          <DateDistanceToNow date={integratorLastUpdatedUtc} />)
        </span>
      );
    return <DateDistanceToNow date={integratorLastUpdatedUtc} />;
  }, [integratorLastUpdatedUtc, persona]);

  const statusPill = useMemo(() => {
    const dealStageStatus = dealStages?.status;
    const allowedStatus = Object.keys(mapStatusPill);
    const isNotDesktop = listMode !== ListMode.desktopList;
    if (
      dealStageStatus &&
      isNotDesktop &&
      (allowedStatus.includes(dealStageStatus) || isAccepted)
    ) {
      if (isKeyOfMapStatusPill(dealStageStatus)) {
        return mapStatusPill[dealStageStatus];
      }
      return isAccepted ? mapStatusPill[acceptedStatus] : null;
    }
    return null;
  }, [listMode, dealStages?.status]);

  return (
    <div
      className={classnames(borderMode, listMode, "close-my-deals-card", {
        "new-deal": !!isNewDeal,
        "progress-checker": !!dealStages
      })}
      data-testid={`${htmlId}-${recordRow}`}
      id={`${htmlId}-${dealId}`}
      onClick={onDealCardClick}
    >
      <div
        className="vehicle-image"
        data-testid={`${htmlId}-${recordRow}-vehicle-image`}
        id={`${htmlId}-${dealId}-vehicle-image`}
      >
        <ImageWithFallback
          alt="selling vehicle associated with the deal"
          data-testid={`${htmlId}-${dealId}-vehicle-img`}
          id={`${htmlId}-${dealId}-vehicle-img`}
          src={sellingVehicle?.vehicleImageUrl || ""}
          style={{ objectFit: "contain", height: "120px" }}
        />
      </div>
      <CcxmReusableLabelledInsightDisplay
        appendedContent={
          (showMultiDealButton || statusPill) && (
            <>
              {showMultiDealButton && (
                <LinkChip
                  className="multi-deal-button"
                  data-testid={`${htmlId}-${recordRow}-multi-deal-button`}
                  id={`${htmlId}-${dealId}-multi-deal-button`}
                  label="+ Show All Deals"
                  onClick={onMultiDealClick}
                />
              )}
              {statusPill && (
                <span
                  className="status-pill"
                  data-testid={`${htmlId}-${recordRow}-${statusPill.id}`}
                  id={`${htmlId}-${dealId}-${statusPill.id}`}
                  style={{ backgroundColor: statusPill.color }}
                >
                  {statusPill.text}
                </span>
              )}
            </>
          )
        }
        className="card-header"
        content={headerContent}
        contentDelimiter={"|"}
        data-testid={`${htmlId}-${recordRow}-header`}
        htmlId={`${htmlId}-${dealId}-header`}
        multiline={
          listMode === ListMode.desktopCustomer ||
          listMode === ListMode.mobileList
        }
      />
      <CcxmReusableLabelledInsightDisplay
        className="vehicle-description"
        content={[
          {
            className: "shrink",
            "data-testid": `${htmlId}-${recordRow}-vehicle-description`,
            id: `${htmlId}-${dealId}-vehicle-description`,
            value: vehicleFullName
          },
          {
            className: "static",
            "data-testid": `${htmlId}-${recordRow}-vehicle-type-and-stock-number`,
            id: `${htmlId}-${dealId}-vehicle-type-and-stock-number`,
            value: vehicleTypeAndStockNumber
          }
        ]}
        contentDelimiter={"|"}
        data-testid={`${htmlId}-${recordRow}-vehicle`}
        displayLabel={
          listMode === ListMode.desktopList || listMode === ListMode.tabletList
        }
        htmlId={`${htmlId}-${dealId}-vehicle`}
        label="Vehicle"
        multiline={
          listMode === ListMode.desktopList || listMode === ListMode.tabletList
        }
      />
      {(listMode === ListMode.desktopList ||
        listMode === ListMode.tabletList) && (
        <EmployeesInsightDisplay
          dealId={dealId}
          employees={employees}
          htmlId={htmlId}
          recordRow={recordRow}
        />
      )}
      <CcxmReusableLabelledInsightDisplay
        className="recent-update"
        content={[
          {
            "data-testid": `${htmlId}-${recordRow}-recent-update-message`,
            id: `${htmlId}-${dealId}-recent-update-message`,
            value: mostRecentUpdateMessage
          },
          {
            "data-testid": `${htmlId}-${recordRow}-last-updated-utc`,
            id: `${htmlId}-${dealId}-last-updated-utc`,
            value: getLastUpdatedUtcValue
          }
        ]}
        data-testid={`${htmlId}-${recordRow}-recent-update`}
        displayLabel={
          listMode === ListMode.desktopList || listMode === ListMode.tabletList
        }
        htmlId={`${htmlId}-${dealId}-card-header`}
        label="Recent Update"
        multiline={
          listMode === ListMode.desktopList || listMode === ListMode.tabletList
        }
      />
      {dealStages && listMode === ListMode.desktopList && (
        <div className="checks" data-testid={`${htmlId}-${recordRow}-checks`}>
          <ProgressChecker
            className="checks-progress"
            data-testid="checks"
            id={`${htmlId}-${recordRow}-progress-checker`}
            steps={dealTracker}
          />
        </div>
      )}
      <div
        className={classnames(
          "actions",
          listMode === ListMode.desktopList ? "right" : "left"
        )}
        data-testid={`${htmlId}-${recordRow}-action-section`}
      >
        {manageDealButtonLink && (
          <>
            <a
              data-testid={`${htmlId}-${recordRow}-manager-view-anchor-tag`}
              href={manageDealButtonLink}
              rel="noreferrer"
              target="_blank"
            >
              <Button
                buttonStyle="tertiary"
                data-testid={`${htmlId}-${recordRow}-manager-view-button`}
                id={`${htmlId}-${dealId}-manager-view-button`}
                size="small"
                startIcon={
                  listMode !== ListMode.mobileList ? (
                    <ArrowTopRightOnSquareIcon
                      data-testid={`${htmlId}-${recordRow}-manager-view-icon`}
                    />
                  ) : null
                }
                onClick={onOpenDealCentralClick}
              >
                Manager View
              </Button>
            </a>
            <span className="delimiter">|</span>
          </>
        )}
        {viewDealButtonLink && (
          <>
            <a
              data-testid={`${htmlId}-${recordRow}-sales-view-anchor-tag`}
              href={viewDealButtonLink}
              rel="noreferrer"
              target="_blank"
            >
              <Button
                buttonStyle="tertiary"
                data-testid={`${htmlId}-${recordRow}-sales-view-button`}
                id={`${htmlId}-${dealId}-sales-view-button`}
                size="small"
                startIcon={
                  listMode !== ListMode.mobileList ? (
                    <ArrowTopRightOnSquareIcon
                      data-testid={`${htmlId}-${recordRow}-sales-view-icon`}
                    />
                  ) : null
                }
                onClick={onOpenShowroomClick}
              >
                Sales View
              </Button>
            </a>
            <span className="delimiter">|</span>
          </>
        )}
        <Button
          buttonStyle="tertiary"
          data-testid={`${htmlId}-${recordRow}-customer-record-button`}
          id={`${htmlId}-${dealId}-customer-record-button`}
          size="small"
          startIcon={
            consumerType === "Business" ? (
              listMode !== ListMode.mobileList ? (
                <BuildingOfficeIcon
                  data-testid={`consumer-${recordRow}-business-icon`}
                />
              ) : null
            ) : listMode !== ListMode.mobileList ? (
              <UserCircleIcon
                data-testid={`consumer-${recordRow}-individual-icon`}
              />
            ) : null
          }
          onClick={onCustomerRecordClick}
        >
          Customer Record
        </Button>
      </div>
    </div>
  );
}

export default Card;
